import React, { createContext, useContext, useState, useCallback } from 'react';

// Cache storage
const globalCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Context
const CacheContext = createContext(null);

// Provider
export const CacheProvider = ({ children }) => {
  const [, forceUpdate] = useState(0);

  const getCache = useCallback((key) => {
    const cached = globalCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    globalCache.delete(key);
    return null;
  }, []);

  const setCache = useCallback((key, data) => {
    globalCache.set(key, { data, timestamp: Date.now() });
    forceUpdate(n => n + 1);
  }, []);

  const invalidateCache = useCallback((pattern = null) => {
    if (pattern === null) {
      globalCache.clear();
    } else {
      for (const key of globalCache.keys()) {
        if (key.includes(pattern)) {
          globalCache.delete(key);
        }
      }
    }
    forceUpdate(n => n + 1);
  }, []);

  const hasCache = useCallback((key) => {
    const cached = globalCache.get(key);
    return cached && Date.now() - cached.timestamp < CACHE_DURATION;
  }, []);

  return (
    <CacheContext.Provider value={{ getCache, setCache, invalidateCache, hasCache }}>
      {children}
    </CacheContext.Provider>
  );
};

// Hook
export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within CacheProvider');
  }
  return context;
};

// Standalone functions for api.js (non-React context)
export const getCacheStandalone = (key) => {
  const cached = globalCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  globalCache.delete(key);
  return null;
};

export const setCacheStandalone = (key, data) => {
  globalCache.set(key, { data, timestamp: Date.now() });
};

export const hasCacheStandalone = (key) => {
  const cached = globalCache.get(key);
  return cached && Date.now() - cached.timestamp < CACHE_DURATION;
};

export const invalidateCacheStandalone = (pattern = null) => {
  if (pattern === null) {
    globalCache.clear();
  } else {
    for (const key of globalCache.keys()) {
      if (key.includes(pattern)) {
        globalCache.delete(key);
      }
    }
  }
};
